class MediaBenefitsSlider extends HTMLElement {
  constructor() {
    super()
    this.onScroll = this.onScroll.bind(this)
    this.onKeydown = this.onKeydown.bind(this)
    this.onBlockSelect = this.onBlockSelect.bind(this)
    this.onPreviousClick = this.onPreviousClick.bind(this)
    this.onNextClick = this.onNextClick.bind(this)
    this.onIndicatorClick = this.onIndicatorClick.bind(this)
  }

  connectedCallback() {
    if (this.initialized) return

    this.track = this.querySelector('[data-media-benefits-track]')
    this.slides = Array.from(this.querySelectorAll('[data-media-benefits-slide]'))
    this.previousButton = this.querySelector('[data-media-benefits-previous]')
    this.nextButton = this.querySelector('[data-media-benefits-next]')
    this.controls = this.querySelector('[data-media-benefits-controls]')
    this.indicatorContainer = this.querySelector('[data-media-benefits-indicators]')
    this.currentPage = 0
    this.slidesPerPage = 1
    this.totalPages = 1
    this.pagePositions = [0]
    this.scrollFrame = null

    if (!this.track || this.slides.length === 0) return

    this.track.addEventListener('scroll', this.onScroll, { passive: true })
    this.track.addEventListener('keydown', this.onKeydown)
    this.previousButton?.addEventListener('click', this.onPreviousClick)
    this.nextButton?.addEventListener('click', this.onNextClick)
    this.indicatorContainer?.addEventListener('click', this.onIndicatorClick)
    document.addEventListener('shopify:block:select', this.onBlockSelect)
    this.resizeObserver = new ResizeObserver(() => this.refreshLayout())
    this.resizeObserver.observe(this.track)
    this.initialized = true
    this.refreshLayout()
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll)
    this.track?.removeEventListener('keydown', this.onKeydown)
    this.previousButton?.removeEventListener('click', this.onPreviousClick)
    this.nextButton?.removeEventListener('click', this.onNextClick)
    this.indicatorContainer?.removeEventListener('click', this.onIndicatorClick)
    document.removeEventListener('shopify:block:select', this.onBlockSelect)
    this.resizeObserver?.disconnect()
    if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame)
    this.initialized = false
    this.scrollFrame = null
  }

  onPreviousClick() {
    this.goToPage(this.currentPage - 1)
  }

  onNextClick() {
    this.goToPage(this.currentPage + 1)
  }

  onIndicatorClick(event) {
    const indicator = event.target.closest('[data-media-benefits-page]')
    if (!indicator || !this.indicatorContainer.contains(indicator)) return
    this.goToPage(Number.parseInt(indicator.dataset.mediaBenefitsPage, 10))
  }

  onScroll() {
    if (this.scrollFrame) return
    this.scrollFrame = window.requestAnimationFrame(() => {
      if (this.pagePositions.length > 0) {
        this.currentPage = this.pagePositions.reduce((closestIndex, position, index) => {
          const closestDistance = Math.abs(this.pagePositions[closestIndex] - this.track.scrollLeft)
          const distance = Math.abs(position - this.track.scrollLeft)
          return distance < closestDistance ? index : closestIndex
        }, 0)
        this.updateControls()
      }
      this.scrollFrame = null
    })
  }

  refreshLayout() {
    const isWideLayout = window.matchMedia('(min-width: 750px)').matches
    const previousFirstSlide = this.currentPage * this.slidesPerPage
    let slidesPerPage = 1

    if (isWideLayout) {
      const styles = window.getComputedStyle(this.track)
      const gap = Number.parseFloat(styles.columnGap) || 0
      const availableWidth = this.track.clientWidth
      slidesPerPage = Math.max(1, Math.floor((availableWidth + gap) / (250 + gap)))
      slidesPerPage = Math.min(slidesPerPage, this.slides.length)
      const cardWidth = (availableWidth - gap * (slidesPerPage - 1)) / slidesPerPage
      this.track.style.setProperty('--media-benefits-card-width', `${cardWidth}px`)
    } else {
      this.track.style.removeProperty('--media-benefits-card-width')
    }

    this.slidesPerPage = slidesPerPage
    this.totalPages = Math.ceil(this.slides.length / this.slidesPerPage)

    const firstSlideOffset = this.slides[0].offsetLeft
    const maximumScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth)
    this.pagePositions = Array.from({ length: this.totalPages }, (_entry, pageIndex) => {
      if (pageIndex === this.totalPages - 1) return maximumScroll
      const slideIndex = pageIndex * this.slidesPerPage
      return Math.min(this.slides[slideIndex].offsetLeft - firstSlideOffset, maximumScroll)
    })

    this.currentPage = Math.min(Math.floor(previousFirstSlide / this.slidesPerPage), this.totalPages - 1)
    this.syncIndicators()
    this.controls?.toggleAttribute('hidden', this.totalPages <= 1)

    if (this.pagePositions.length > 0) {
      this.track.scrollTo({ left: this.pagePositions[this.currentPage], behavior: 'auto' })
    }

    this.updateControls()
  }

  syncIndicators() {
    if (!this.indicatorContainer) return

    if (this.indicatorContainer.children.length !== this.totalPages) {
      const indicators = Array.from({ length: this.totalPages }, (_entry, pageIndex) => {
        const indicator = document.createElement('button')
        indicator.type = 'button'
        indicator.className = 'media-benefits__indicator slider-indicator'
        indicator.dataset.mediaBenefitsPage = pageIndex
        indicator.setAttribute('aria-label', `${this.track.getAttribute('aria-label')} ${pageIndex + 1}`)
        indicator.setAttribute('aria-controls', this.track.id)
        return indicator
      })
      this.indicatorContainer.replaceChildren(...indicators)
    }
  }

  onKeydown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      this.goToPage(this.currentPage - 1)
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault()
      this.goToPage(this.currentPage + 1)
    }
  }

  onBlockSelect(event) {
    const index = this.slides.findIndex((slide) => slide.contains(event.target) || slide === event.target)
    if (index >= 0) this.goToPage(Math.floor(index / this.slidesPerPage))
  }

  goToPage(pageIndex) {
    const nextPage = Math.max(0, Math.min(pageIndex, this.totalPages - 1))
    this.currentPage = nextPage
    this.track.scrollTo({
      left: this.pagePositions[nextPage],
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    })
    this.updateControls()
  }

  updateControls() {
    this.previousButton?.toggleAttribute('disabled', this.currentPage === 0)
    this.nextButton?.toggleAttribute('disabled', this.currentPage === this.totalPages - 1)
    Array.from(this.indicatorContainer?.children || []).forEach((indicator, pageIndex) => {
      const isCurrent = pageIndex === this.currentPage
      indicator.classList.toggle('slider-indicator--active', isCurrent)
      indicator.setAttribute('aria-current', isCurrent ? 'true' : 'false')
      indicator.setAttribute('tabindex', isCurrent ? '0' : '-1')
    })
  }
}

if (!customElements.get('media-benefits-slider')) {
  customElements.define('media-benefits-slider', MediaBenefitsSlider)
}
